import React, { memo, useEffect } from 'react';
// Hooks
import { AdjustableComponent } from '../../../../../utils/hooks/useClasses';
import { useForm } from 'plume-web-utils';
// Component
import {
  Button,
  InputField,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  notify,
} from 'plume-ui';
import { activePartnerAtom, emailModalAtom } from '../../../../../state';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { ModalStyles } from 'plume-ui/dist/components/Modal/Modal';
import FormattedMessage from '../../../../../utils/components/FormattedMessage';
import { useTranslation } from 'react-i18next';
import { DependencyContainer } from '../../../../../DependencyContainer';
import { ConfigurationEmailPayload } from '../../../../Monitor/types';
import validateEmail from '../../../../../utils/isEmail/validateEmail';
import { useTrackEvent } from '../../../../trackingAnalytics/hooks/useTrackEvent';
import { MixPanelEvents } from '../../../../../mixPanelEvents';
import { AxiosError } from 'axios';
import { ErrorResponse } from '../../../../../types';
import EmailLanguageSelect from '../EmailLanguageSelect';

type EmailModalProps = {};
type EmailModalStyles = {};
type EmailFormValues = Pick<
  ConfigurationEmailPayload,
  'email' | 'name' | 'language'
>;

const { outreachService } = new DependencyContainer();

const EmailModal: AdjustableComponent<
  EmailModalProps,
  EmailModalStyles
> = () => {
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();
  const modalProps = useRecoilValue(emailModalAtom);
  const resetModal = useResetRecoilState(emailModalAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const [saving, setSaving] = React.useState(false);

  const form = useForm<EmailFormValues>({
    initialValues: {
      email: modalProps.email.email || '',
      name: modalProps.email.name || '',
      language: modalProps.email.language || '',
    },
    validationSchema: {
      email: {
        required: true,
        validator: validateEmail,
      },
      language: {
        required: true,
      },
    },
    validateOnChange: true,
  });

  const handleFinishError = (message?: string) => {
    notify({
      title: t(
        `configuration.notifications.error.email.${modalProps.type}.title`,
      ),
      body: message
        ? message
        : t(`configuration.notifications.error.email.${modalProps.type}.body`),
      type: 'error',
    });
    trackEvent({
      eventName: MixPanelEvents.EMAIL_FAILURE,
      additionalContent: {
        INTERACTION: modalProps.type.toUpperCase(),
        EMAIL_ID: modalProps.type === 'edit' ? modalProps.email.id : undefined,
        ERROR_MESSAGE: message || 'no message',
      },
    });
    setSaving(false);
  };

  const handleFinishClick = async () => {
    if (saving || !form.validateForm()) {
      return;
    }

    setSaving(true);

    if (!activePartner?.id) {
      handleFinishError();
      return;
    }

    const emailData = {
      ...modalProps.email,
      ...form.values,
      name: form.values.name === '' ? form.values.email : form.values.name,
    };

    try {
      if (modalProps.type === 'edit' && modalProps.email.id) {
        await outreachService.updateEmail(activePartner.id, emailData);
      } else {
        await outreachService.saveEmail(activePartner.id, emailData);
      }

      notify({
        title: t(
          `configuration.notifications.success.email.${modalProps.type}.title`,
        ),
        body: t(
          `configuration.notifications.success.email.${modalProps.type}.body`,
        ),
        type: 'success',
      });

      trackEvent({
        eventName: MixPanelEvents.EMAIL_SUCCESS,
        additionalContent: {
          INTERACTION: modalProps.type.toUpperCase(),
          EMAIL_ID:
            modalProps.type === 'edit' ? modalProps.email.id : undefined,
        },
      });

      modalProps.refetchEmails();
      setSaving(false);
      resetModal();
    } catch (error) {
      handleFinishError(
        (error as AxiosError<ErrorResponse>).response?.data?.message,
      );
    }
  };

  useEffect(() => {
    form.setValues({
      email: modalProps.email.email || '',
      name: modalProps.email.name || '',
      language: modalProps.email.language || '',
    });
  }, [modalProps]);

  return (
    <Modal
      classes={(current: ModalStyles) => ({
        root: `${current.root} Configuration__modal Configuration__modal--url-email`,
        title: ``,
      })}
      isOpen={modalProps.isOpen}
      onRequestClose={resetModal}
      ariaHideApp={false}
    >
      <ModalHeader
        title={
          modalProps.type === 'add'
            ? t('configuration.modals.email.add')
            : t('configuration.modals.email.edit')
        }
      />
      <ModalBody>
        <InputField
          classes={(current) => ({
            ...current,
            root: `${current.root} ${
              form.values.name === '' ? 'InputField--empty' : ''
            }`,
          })}
          label={t('configuration.modals.email.name')}
          id="input-name"
          value={form.values.name}
          onChange={(event) => form.handleChange('name', event.target.value)}
          onBlur={() => form.handleBlur('name')}
        />
        <InputField
          classes={(current) => ({
            ...current,
            root: `${current.root} ${
              form.values.email === '' ? 'InputField--empty' : ''
            }`,
          })}
          label={t('configuration.modals.email.email')}
          id="input-email"
          required
          requiredAsterix
          value={form.values.email}
          onChange={(event) => form.handleChange('email', event.target.value)}
          onBlur={() => form.handleBlur('email')}
        />
        <EmailLanguageSelect
          onChange={(value) => form.handleChange('language', String(value))}
          value={form.values.language}
        />
      </ModalBody>
      <ModalFooter>
        <Button styleVariant="tertiary-grey" onClick={resetModal}>
          <FormattedMessage id="confirmation.cancel" />
        </Button>
        <Button
          styleVariant="superprimary"
          onClick={handleFinishClick}
          disabled={!form.isValid || saving}
        >
          <FormattedMessage id="confirmation.finish" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default memo(EmailModal);
